import { useEffect, useState } from "react";
import { computeProgress, progressBand } from "../lib/appointments";

/**
 * ProgressBar — Live progress for an in-progress appointment.
 * Only renders when appointment.status === 'em-andamento'.
 * Color bands: 0-80% green, 80-100% amber, >100% red.
 *
 * Variants:
 *   - thin (4px): for compact cards
 *   - medium (6px): default
 *   - thick (10px): for hero cards
 */
const COLORS = {
    safe: { bar: "#34d399", glow: "rgba(52,211,153,0.45)" },
    warning: { bar: "#fbbf24", glow: "rgba(251,191,36,0.5)" },
    danger: { bar: "#f87171", glow: "rgba(248,113,113,0.5)" },
};

const HEIGHT = {
    thin: "h-1",
    medium: "h-1.5",
    thick: "h-2.5",
};

const TickProgressBar = ({ appointment, variant = "medium", showLabel = false, testId }) => {
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        if (appointment?.status !== "em-andamento") return undefined;
        const t = setInterval(() => setNow(Date.now()), 1000 * 15); // refresh every 15s
        return () => clearInterval(t);
    }, [appointment?.status]);

    if (!appointment || appointment.status !== "em-andamento" || !appointment.started_at) {
        return null;
    }

    const progress = computeProgress(appointment, now);
    const band = progressBand(progress);
    const color = COLORS[band];
    const pct = Math.min(1, progress) * 100;
    const overrun = progress > 1 ? Math.min((progress - 1) * 100, 50) : 0;

    const elapsedMin = Math.floor((now - appointment.started_at) / 60000);
    const remainingMin = Math.max(0, Math.round(appointment.duration_min - elapsedMin));

    return (
        <div data-testid={testId || "progress-bar"} className="w-full">
            {showLabel && (
                <div className="flex items-center justify-between text-[10px] uppercase tracking-wider mb-1">
                    <span style={{ color: color.bar }} className="font-bold font-mono">
                        {Math.round(progress * 100)}%
                    </span>
                    <span className="text-amber-200/70 font-mono">
                        {band === "danger"
                            ? `+${Math.abs(remainingMin === 0 ? Math.round(elapsedMin - appointment.duration_min) : 0)} min atrasado`
                            : `${remainingMin} min restante`}
                    </span>
                </div>
            )}
            <div className={`relative w-full ${HEIGHT[variant]} bg-black/70 rounded-full overflow-hidden border border-amber-500/15`}>
                <div
                    className="absolute inset-y-0 left-0 rounded-full transition-all duration-500 ease-out"
                    style={{
                        width: `${pct}%`,
                        backgroundColor: color.bar,
                        boxShadow: `0 0 10px ${color.glow}`,
                    }}
                ></div>
                {overrun > 0 && (
                    <div
                        className="absolute inset-y-0 right-0 rounded-full animate-pulse"
                        style={{
                            width: `${overrun}%`,
                            background: `repeating-linear-gradient(45deg, ${color.bar}, ${color.bar} 4px, rgba(0,0,0,0.3) 4px, rgba(0,0,0,0.3) 8px)`,
                        }}
                    ></div>
                )}
            </div>
        </div>
    );
};

export default TickProgressBar;
